import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';

import { CustomThemeProvider } from './contexts/ThemeContext';
import { AlertProvider } from './contexts/AlertContext';
import { AccountProvider, useAccount } from './contexts/AccountContext';

import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Dashboard from './pages/Dashboard';
import FreightRecords from './pages/FreightRecords';
import Reports from './pages/Reports';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import UserManagement from './pages/UserManagement';
import SiteAdminDashboard from './pages/SiteAdminDashboard';
import FreightRecordReview from './pages/FreightRecordReview';
import AddFreight from './pages/AddFreight';
import Companies from './pages/Companies';
import AccountManager from './pages/AccountManager';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SettingsPage from './pages/Settings';

const AppContent = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Start open by default
  const [isAuth, setAuth] = useState(false);

  const handleMobileDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);

  // Check if current route is an auth page
  const isAuthPage = ['/login', '/signup', '/forgot-password'].includes(location.pathname);

  // Load sidebar state from localStorage on mount
  useEffect(() => {
    const savedSidebarState = localStorage.getItem('loadoc-sidebar-open');
    if (savedSidebarState !== null) {
      setSidebarOpen(JSON.parse(savedSidebarState));
    }
  }, []);

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('loadoc-sidebar-open', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Use AccountContext instead of separate auth listener
  const { user, isImpersonating, impersonatedUser } = useAccount();

  useEffect(() => {
    setAuth(!!user);
  }, [user]);

  // For auth pages, render without layout wrapper
  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
    );
  }

  // For authenticated pages, render with full layout
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1 }}>
        {isAuth && (
          <TopBar
            onMobileMenuClick={handleMobileDrawerToggle}
            sidebarOpen={sidebarOpen}
          />
        )}

        {isAuth && (
          <Sidebar
            open={sidebarOpen}
            onToggle={handleSidebarToggle}
            mobileOpen={mobileOpen}
            onMobileToggle={handleMobileDrawerToggle}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme) => theme.palette.background.default,
            transition: (theme) => theme.transitions.create(['margin-left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ml: {
              xs: 0,
              md: 0
            },
            overflow: 'auto',
            // Add top padding to account for the floating user menu and impersonation banner
            pt: {
              xs: isImpersonating && impersonatedUser ? 13 : 7, // Extra padding for impersonation banner on mobile
              md: isImpersonating && impersonatedUser ? 7 : 1   // Extra padding for impersonation banner on desktop
            },
            // Reduced right padding to avoid floating user menu overlap while maximizing content width
            pr: { xs: 1, md: 3 }, // Reduced from 20 to 3 for better space utilization
          }}
        >
          <Box sx={{
            flexGrow: 1,
            width: '100%',
            px: { xs: 1, md: 2 }, // Responsive horizontal padding
            py: { xs: 1, md: 2 }, // Responsive vertical padding
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/records"
                element={
                  <ProtectedRoute>
                    <FreightRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/records/add"
                element={
                  <ProtectedRoute>
                    <AddFreight />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/records/review/:recordId"
                element={
                  <ProtectedRoute>
                    <FreightRecordReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute>
                    <Companies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/:tab"
                element={
                  <ProtectedRoute>
                    <SiteAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <SiteAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h4">404 - Page Not Found</Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      The page you're looking for doesn't exist.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                      onClick={() => window.location.href = '/'}
                    >
                      Go to Home
                    </Button>
                  </Box>
                }
              />
            </Routes>
          </Box>

          {/* Footer */}
          {isAuth && <Footer />}
        </Box>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <CustomThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AlertProvider>
          <AccountProvider>
            <Router>
              <CssBaseline />
              <AppContent />
            </Router>
          </AccountProvider>
        </AlertProvider>
      </LocalizationProvider>
    </CustomThemeProvider>
  );
};

export default App;
